<template>
  <section
    class="productHeader bg-off-white sm"
    :class="[ props.data.parentClasses, props.data.parentStyle ? props.data.parentStyle : 'defaultStyle' ]"
  >
    <div class="container col">
      <div class="row">
        <div
          class="col"
          :class="props.data.flex[0]"
        >
          <div
            v-if="props.data.heroImage.image && props.data.heroImage.image.original"
            class="productHeader__image"
            :class="{'hide-on-med-and-down': props.data.parentStyle === 'defaultStyle'}"
          >
            <NuxtPicture
              preload
              :img-attrs="{class: 'responsive-img', alt: props.data.heroImage.image.text}"
              :src="props.data.heroImage.image.web ? props.data.heroImage.image.web : props.data.heroImage.image.original"
              height="500"
              width="800"
              :placeholder="[100, 60, 40]"
            />
          </div>
        </div>
        <div
          class="col valign-wrapper"
          :class="props.data.flex[1]"
        >
          <div class="productHeader__info">
            <div
              v-if="props.data.reviewsEnable"
              class="productHeader__info__reviews justify-between"
            >
              <HelpersReviews />
            </div>

            <h1
              class="title productHeader__info__title primary-color-text text-base-2"
              v-html="props.data.heroImage.heading"
            >
            </h1>

            <p
              v-if="props.data.priceEnable && props.productData.is_set && props.productData.pre_populated_unit_price && !query['utm_source']?.length"
              class="productHeader__info__price price mt-4"
            >
              <strong>{{ moneyFormat(props.productData.pre_populated_unit_price) }}</strong>
              <span class="grey-text text-darken-1"> / {{ $t('giftLower') }}</span>
            </p>

            <div
              v-if="props.data.heroImage.image && props.data.heroImage.image.original"
              class="productHeader__info__image hide-on-large-only"
              :class="{'hide': props.data.parentStyle !== 'defaultStyle'}"
            >
              <nuxt-picture
                preload
                loading="lazy"
                :img-attrs="{class: 'responsive-img', alt: props.data.heroImage.image.text}"
                :src="props.data.heroImage.image.original"
                :placeholder="[100, 50, 10]"
                height="420"
                width="670"
                sizes=""
              />
            </div>

            <hr
              v-if="props.data.parentStyle === 'defaultStyle'"
              class="hide-on-med-and-down"
            >

            <div class="cta-block">
              <div
                v-if="props.data.mainInfoEnable && props.data.parentStyle === 'defaultStyle'"
                class="cta-sample-block flex-column grey-text"
              >
                <p class="info small">
                  {{ $t('estimatedDelivery') }} <strong>{{ props.options.settingsData.production_time.customer_production_time_number }} {{ $t(props.options.settingsData.production_time.customer_production_time_display) }}</strong>
                </p>
                <p class="info small">
                  {{ $t('minimumOrder') }} <strong>{{ props.options.settingsData.quantity.order_min_quantity }} {{ $t('giftLower') }}</strong>
                </p>
                <p
                  v-if="props.productData.pre_populated_unit_price"
                  class="info small"
                >
                  {{ $t('estimatedPricePerGift') }} <strong>{{ moneyFormat(props.productData.pre_populated_unit_price) }}</strong>
                </p>
              </div>

              <div class="productHeader__info__actions flex-column cta-block-item">
                <PagesProductActionsForm
                  :data="{
                    price: props.productData.pre_populated_unit_price,
                    priceEnable: props.data.priceEnable && query['utm_source']?.length,
                    actionBtns: props.data.actionBtns,
                    startUpMaxOrderQty: props.options.settingsData.is_quote_only ? props.options.settingsData.quantity.quote_min_quantity : props.options.settingsData.quantity.order_min_quantity,
                    productOrderMax: props.data.heroImage.product_order_max,
                    finalPricePerUnit: props.data.heroImage.finalPricePerUnit,
                    priceShown: props.data.heroImage.priceShown,
                    isFromAdvertising: props.data.heroImage.isFromAdvertising,
                    shippingText: props.data.heroImage.shippingText
                  }"
                  :product-data="props.productData"
                  :product-spec-data="props.productSpecData"
                  :options="props.options"
                  @return-for-gtm="e => {
                    emit('returnForGtm', e)
                  }"
                />
              </div>

              <ul
                v-if="props.data.mainInfoEnable && props.data.parentStyle === 'secondStyle'"
                class="cta-sample-block check-list alt-icon pt-0 flex-column"
              >
                <li class="info bold">
                  {{ $t('everyGiftStarts') }}
                </li>
                <li class="info bold">
                  {{ $t('designedAndHandcrafted') }}
                </li>
                <li class="info bold">
                  {{ $t('noMinimums') }}
                </li>
                <li class="info bold">
                  {{ $t('expectedDeliveryIn') }} <strong>{{ props.options.settingsData.production_time.customer_production_time_number }} {{ $t(props.options.settingsData.production_time.customer_production_time_display) }}</strong>.
                </li>
              </ul>
            </div>

            <div class="productHeader__info__timeStripe row ml-0 mr-0">
              <HelpersTimeStripe 
                v-if="props.data.timeStripeEnable && !props.options.settingsData.is_quote_only"
                :data="estShipDate"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="props.data.advantagesEnable && props.data.advantagesBadgesFilters.list.length > 0"
        class="productHeader__advantages"
      >
        <hr>
        <WidgetsPagesAdvantagesProduct
          :data="{
            items: props.data.advantagesBadgesFilters.list
          }"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
const { query } = useRoute()
const estShipDate = ref(false)

/**
* Props
*/
const props = defineProps({
  data: {
    type: [Object],
    default: null
  },
  productData: {
    type: [Object],
    default: null
  },
  productSpecData: {
    type: [Object],
    default: null
  },
  options: {
    type: [Object],
    default: null
  }
})

/**
* Emits
*/
const emit = defineEmits(['returnForGtm'])

/**
* Get Est Ship Date
*/
watch(useInterval(1000), () => {
  if (props.options.globalData && props.options.settingsData) {
    estShipDate.value = getEstShipDate({
      cutOffTime: props.options.globalData.cut_off_time,
      timeRestriction: props.productData.delivery_time_list.min,
      timeRestrictionsArr: props.productData.delivery_time_list,
      isShort: true
    })
  }
})
</script>

<style lang="scss" scoped>
.productHeader {
  padding-top: 0;
  &__image {
    &:deep(img) {
      width: 100%;
    }
  }
  &__info {
    width: 100%;
    padding: 1rem 0;

    .cta-block {
      margin-bottom: 1.5rem;
      @media only screen and (min-width: 993px) {
        margin: 2rem auto 1.5rem;
      }
      .cta-sample-block {
        margin-bottom: 1rem;
        .info {
          padding: .5rem 0;
          margin: 0;
          border-bottom: 1px solid #dcdde1;
          &:last-child {
            border-bottom: none;
          }
        }
      }
    }
    &__reviews {
      &:deep(.reviewsBlock) {
        display: flex;
        flex-wrap: wrap;
        margin: 0 -.5rem;
        @media only screen and (max-width: 993px) {
          justify-content: center;
          padding-top: 2rem;
        }
        .sa-widget-wrapper,
        .tp-widget-wrapper {
          padding: 0 .5rem;
          margin: 0;
        }
      }
    }
    &__title {
      margin: 10px 0;
      font-size: 3rem;
      font-weight: 500;
      line-height: 1.25;
      @media only screen and (min-width: 993px) {
        margin: 1.25rem 0;
      }
      @media only screen and (max-width: 993px) {
        text-align: center;
      }
    }
    &__price {
      @media only screen and (max-width: 993px) {
        text-align: center;
      }
    }
    &__image {
      &:deep(img) {
        width: 100%;
      }
    }
    &__actions {
      &:deep(.tiny strong) {
        color: color("primary-color", "base-2");
      }
    }
    &__timeStripe {
      @media only screen and (max-width: 993px) {
        justify-content: center;
      }
      &:deep(.time-stripe) {
        width: max-content;
        padding: .25rem 1rem;
        margin-bottom: 0;
        color: color("primary-color", "base-2");
        font-size: 11px;
        line-height: 1.5;
        background-color: rgba(1,82,135,.05);
        border-radius: 60px;
        i {
          color: color("primary-color", "base");
          font-size: 1.25rem;
          vertical-align: sub;
          @media only screen and (min-width: 993px) {
            margin-right: .25rem;
            font-size: 1.1875rem;
          }
        }
        time {
          font-weight: 700;
        }
      }
    }
  }

  /*
  * Second Style
  */
  &.secondStyle {
    .productHeader {
      &__info {
        width: 100%;
        margin: 1.5rem auto;
        text-align: left;
        @media only screen and (min-width: 1441px) {
          max-width: 75%;
        }

        &__title {
          margin-bottom: 0;
          font-size: 1.5rem;
          font-weight: 400;
          @media only screen and (max-width: 993px) {
            margin-bottom: 1rem;
            text-align: center;
          }
        }

        .cta-block {
          .cta-sample-block {
            font-size: .9rem;
            @media only screen and (max-width: 993px) {
              text-align: center;
            }
            .info {
              border-bottom: none;
            }
          }
        }

        &:deep(.cta-block) {
          .cta-form {
            & > .cta-block-item {
              flex-direction: column;
              .btn-holder {
                flex: 1 1 100%;
                width: 100%;
              }
              .btn-separate {
                padding-bottom: 0;
                color: #8e8e8e;
                font-size: 1rem;
                text-transform: uppercase;
              }
            }
          }
        }

        .check-list {
          li {
            &:before {
              content: "❖";
              padding: 0;
              margin: 0 .375rem 0 0;
              color: rgba(142,142,142,.5);
              font-size: 1rem;
              vertical-align: text-bottom;
            }
          }
        }
      }
    }
  }
}
</style>
